import { state } from './state.js';
import { STYLES_DATA } from './styles-data.js';
import { showToast, escapeHtml, formatFileSize, loadProjectIntoScene, duplicateProject } from './project-scene.js';

export function getStoredOwnerKey() {
  try {
    return localStorage.getItem('vas.ownerKey') || '';
  } catch {
    return '';
  }
}

export function setStoredOwnerKey(key) {
  try {
    if (key) {
      localStorage.setItem('vas.ownerKey', key);
    } else {
      localStorage.removeItem('vas.ownerKey');
    }
  } catch {}
}

export function getProjectApiHeaders(extraHeaders = {}) {
  const headers = { ...extraHeaders };
  const key = getStoredOwnerKey();
  if (key) {
    headers['X-Owner-Key'] = key;
  }
  return headers;
}

export function updateRecoveryUi() {
  const key = getStoredOwnerKey();
  const displayInput = document.getElementById('owner-key-display');
  if (displayInput) {
    displayInput.value = key;
  }
}

let isRecoveryUiInitialized = false;

export function initRecoveryKeyUi() {
  if (isRecoveryUiInitialized) return;
  const btnToggle = document.getElementById('btn-toggle-show-key');
  const displayInput = document.getElementById('owner-key-display');
  if (btnToggle && displayInput) {
    btnToggle.addEventListener('click', () => {
      if (displayInput.type === 'password') {
        displayInput.type = 'text';
        btnToggle.textContent = '🙈';
      } else {
        displayInput.type = 'password';
        btnToggle.textContent = '👁️';
      }
    });
  }

  const btnCopy = document.getElementById('btn-copy-owner-key');
  if (btnCopy) {
    btnCopy.addEventListener('click', async () => {
      const key = getStoredOwnerKey();
      if (!key) {
        showToast('⚠️ Bạn chưa có mã khôi phục nào!', 'info');
        return;
      }
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(key);
        } else if (displayInput) {
          displayInput.select();
          document.execCommand('copy');
        }
        showToast('📋 Đã sao chép mã khôi phục vào bộ nhớ tạm!', 'success');
      } catch {
        showToast('📋 Đã sao chép mã khôi phục!', 'success');
      }
    });
  }

  const btnApply = document.getElementById('btn-apply-owner-key');
  const restoreInput = document.getElementById('input-restore-owner-key');
  if (btnApply && restoreInput) {
    btnApply.addEventListener('click', async () => {
      const keyToRestore = restoreInput.value.trim();
      if (!keyToRestore) {
        showToast('⚠️ Vui lòng nhập mã khôi phục từ máy khác!', 'error');
        restoreInput.focus();
        return;
      }

      try {
        const res = await fetch('/api/projects/owner', {
          headers: { 'X-Owner-Key': keyToRestore }
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || 'Mã khôi phục không hợp lệ hoặc kho không tồn tại');
        }
        setStoredOwnerKey(keyToRestore);
        updateRecoveryUi();
        restoreInput.value = '';
        showToast('✨ Đã kích hoạt kho dự án thành công!', 'success');
        await fetchProjects();
      } catch (err) {
        showToast(`❌ ${err.message}`, 'error');
      }
    });
  }

  isRecoveryUiInitialized = true;
}

export function updateFilterCounts() {
  const counts = { all: state.projectsList.length, ghibli: 0, pixel: 0, paper: 0, cyber: 0 };
  state.projectsList.forEach((p) => {
    const s = (p.style || '').toLowerCase();
    if (counts[s] !== undefined) counts[s]++;
  });
  ['all', 'ghibli', 'pixel', 'paper', 'cyber'].forEach((f) => {
    const el = document.getElementById(`filter-count-${f}`);
    if (el) el.textContent = counts[f];
  });
}

/**
 * Hiển thị danh sách card trong Explorer
 */
export function renderProjectGrid() {
  const container = document.getElementById('project-cards-container');
  const emptyState = document.getElementById('project-empty-state');
  if (!container) return;

  let filtered = state.projectsList;

  if (state.currentProjectFilter !== 'all') {
    filtered = filtered.filter((p) => (p.style || '').toLowerCase() === state.currentProjectFilter);
  }

  if (state.currentSearchKeyword.trim() !== '') {
    const kw = state.currentSearchKeyword.toLowerCase();
    filtered = filtered.filter(
      (p) =>
        (p.name || '').toLowerCase().includes(kw) ||
        (p.description || '').toLowerCase().includes(kw) ||
        (p.filename || '').toLowerCase().includes(kw)
    );
  }

  if (filtered.length === 0) {
    container.innerHTML = '';
    if (emptyState) emptyState.style.display = 'flex';
    return;
  }

  if (emptyState) emptyState.style.display = 'none';

  container.innerHTML = filtered
    .map((p) => {
      const isActive = p.filename === state.selectedProjectFilename;
      const styleKey = (p.style || 'ghibli').toLowerCase();
      const styleName =
        styleKey === 'pixel'
          ? 'Pixel Art'
          : styleKey === 'paper'
            ? 'Paper Cut'
            : styleKey === 'cyber'
              ? 'Cyberpunk'
              : 'Ghibli';
      const camName = p.cameraType === 'perspective' ? 'Persp' : 'Ortho';
      const dateStr = p.updatedAt
        ? new Date(p.updatedAt).toLocaleDateString('vi-VN', {
            hour: '2-digit',
            minute: '2-digit',
            day: '2-digit',
            month: '2-digit'
          })
        : '';

      const thumbHtml = p.thumbnail
        ? `<img class="card-thumb-img" src="${p.thumbnail}" alt="${escapeHtml(p.name)}" loading="lazy" />`
        : `<div class="card-thumb-fallback">🎬</div>`;

      const sampleBadgeHtml = p.readOnly ? '<span class="badge-sample" data-badge="sample">Mẫu</span>' : '';

      return `
    <div class="project-card ${isActive ? 'active' : ''}" data-filename="${escapeHtml(p.filename)}">
      <div class="card-thumb-wrap">
        ${thumbHtml}
        <div class="card-top-badges">
          ${sampleBadgeHtml}
          <span class="badge-style ${styleKey}">${styleName}</span>
          <span class="badge-cam">${camName}</span>
        </div>
      </div>
      <div class="card-info">
        <div class="card-name" title="${escapeHtml(p.name)}">${escapeHtml(p.name)}</div>
        <div class="card-meta">
          <span class="card-meta-date">🕒 ${dateStr}</span>
          <span class="u-mono">${formatFileSize(p.size)}</span>
        </div>
      </div>
    </div>
  `;
    })
    .join('');

  container.querySelectorAll('.project-card').forEach((card) => {
    card.addEventListener('click', () => {
      const fn = card.getAttribute('data-filename');
      selectProject(fn);
    });
  });
}

/**
 * Chọn một dự án và hiển thị trong Inspector.
 * Inspector dựng từ bản tóm tắt trong danh sách; file đầy đủ chỉ được đọc khi
 * người dùng thao tác (Nạp / Nhân bản / Tải JSON), nên việc tự chọn dự án mới
 * nhất lúc tải trang không phát sinh request tới một file có thể vừa bị xóa.
 */
export function selectProject(filename) {
  state.selectedProjectFilename = filename;
  document.querySelectorAll('.project-card').forEach((card) => {
    card.classList.toggle('active', card.getAttribute('data-filename') === filename);
  });

  const pSummary = state.projectsList.find((p) => p.filename === filename);
  renderProjectInspector(pSummary || null);
}

/**
 * Đọc dữ liệu đầy đủ của một dự án. Nếu file đã biến mất (bị xóa ở tab khác),
 * báo lỗi và làm mới danh sách thay vì dùng bản tóm tắt thiếu dữ liệu.
 */
async function fetchProjectData(filename) {
  try {
    const res = await fetch(`/api/projects/${encodeURIComponent(filename)}`, {
      headers: getProjectApiHeaders()
    });
    if (!res.ok) throw new Error(res.status === 404 ? 'Tệp dự án không còn tồn tại' : `HTTP ${res.status}`);
    return await res.json();
  } catch (err) {
    showToast(`❌ Không thể đọc dự án: ${err.message}`, 'error');
    await fetchProjects();
    return null;
  }
}

/**
 * Render thông tin chi tiết vào Project Inspector
 */
export function renderProjectInspector(project) {
  const pane = document.getElementById('project-inspector-pane');
  if (!pane) return;

  if (!project) {
    pane.innerHTML = `
    <div class="empty-state empty-state--compact">
      <div class="empty-icon">📁</div>
      <div class="empty-text">Chưa chọn dự án nào</div>
      <div class="empty-hint">Bấm vào một thẻ dự án bên trái để xem chi tiết và nạp vào Studio.</div>
    </div>
  `;
    return;
  }

  const styleKey = (project.environment?.style || project.style || 'ghibli').toLowerCase();
  const styleData = STYLES_DATA[styleKey] || STYLES_DATA.ghibli;
  const camType = project.camera?.type || project.cameraType || 'orthographic';
  const camMode = project.camera?.mode || 'parallax';
  const zSpacing = project.camera?.zSpacing !== undefined ? project.camera.zSpacing : 1.0;
  const paveType = project.environment?.pavement || 'terrazzo';
  const paveName = paveType === 'terrazzo' ? 'Terrazzo Mắt Nai' : paveType === 'stone' ? 'Đá Cubic Cổ' : 'Nhựa Đường';
  const avatarPreset = project.avatar?.preset || project.avatarPreset || 'CO_GAI_AO_DAI';
  const avatarName =
    avatarPreset === 'CO_GAI_AO_DAI'
      ? 'Áo Dài Nón Lá'
      : avatarPreset === 'NGUOI_BAN_TRA_DA'
        ? 'Bác Quán Trà Đá'
        : 'Cô Giáo Duyên Dáng';
  const animName = project.avatar?.anim || 'walk';
  const dateStr = project.updatedAt ? new Date(project.updatedAt).toLocaleString('vi-VN') : 'Không rõ';

  const previewImg = project.thumbnail
    ? `<img class="inspector-preview-img" src="${project.thumbnail}" alt="${escapeHtml(project.name)}" />`
    : `<div class="card-thumb-fallback card-thumb-fallback--full">🎬</div>`;

  const deleteBtnHtml = project.readOnly
    ? ''
    : `
      <button class="btn-action-sub btn-delete" id="btn-inspector-delete" title="Xóa vĩnh viễn tệp dự án này">
        <span class="spec-icon">🗑️</span>
        <span>Xóa Bỏ</span>
      </button>
    `;

  pane.innerHTML = `
  <div class="inspector-preview-box">
    ${previewImg}
  </div>
  <div class="inspector-header-info">
    <div class="inspector-title">${escapeHtml(project.name || project.filename)}</div>
    <div class="inspector-filename">
      <span>📄</span> <code>${escapeHtml(project.filename)}</code>
    </div>
  </div>
  <div class="inspector-desc">
    ${escapeHtml(project.description || 'Không có mô tả chi tiết cho bối cảnh này.')}
  </div>

  <div class="inspector-specs-grid">
    <div class="spec-item">
      <span class="spec-label">Phong Cách</span>
      <span class="spec-val">${styleData.icon} ${styleData.name}</span>
    </div>
    <div class="spec-item">
      <span class="spec-label">Phép Chiếu Ống Kính</span>
      <span class="spec-val">${camType === 'orthographic' ? '📐 Ortho (Song Song)' : '🎥 Persp (Phối Cảnh)'}</span>
    </div>
    <div class="spec-item">
      <span class="spec-label">Chế Độ Thị Sai</span>
      <span class="spec-val">${camMode === 'parallax' ? '🎬 Parallax' : camMode === 'orbit' ? '🔄 3D Orbit' : '📐 Cắt Ngang 90°'}</span>
    </div>
    <div class="spec-item">
      <span class="spec-label">Giãn Cách Z (Depth)</span>
      <span class="spec-val">Z: ${typeof zSpacing === 'number' ? zSpacing.toFixed(2) : zSpacing}x</span>
    </div>
    <div class="spec-item">
      <span class="spec-label">Mặt Sàn Vỉa Hè</span>
      <span class="spec-val">${paveName}</span>
    </div>
    <div class="spec-item">
      <span class="spec-label">Nhân Vật & Cử Động</span>
      <span class="spec-val">${avatarName} • ${animName}</span>
    </div>
    <div class="spec-item spec-item--span-2">
      <span class="spec-label">Cập Nhật Lần Cuối</span>
      <span class="spec-val spec-val--sub">${dateStr}</span>
    </div>
  </div>

  <div class="inspector-actions">
    <button class="btn-action-main" id="btn-inspector-open" title="Nạp ngay bối cảnh này vào Studio">
      <span>🚀</span> <span>Nạp Vào Studio (Open)</span>
    </button>
    <div class="action-secondary-row">
      <button class="btn-action-sub" id="btn-inspector-duplicate" title="Tạo một bản sao mới của dự án">
        <span class="spec-icon">📋</span>
        <span>Nhân Bản</span>
      </button>
      <button class="btn-action-sub" id="btn-inspector-export" title="Tải file JSON về máy tính">
        <span class="spec-icon">⬇️</span>
        <span>Tải JSON</span>
      </button>
      ${deleteBtnHtml}
    </div>
  </div>
`;

  document.getElementById('btn-inspector-open').addEventListener('click', async () => {
    const fullData = await fetchProjectData(project.filename);
    if (!fullData) return;
    await loadProjectIntoScene(fullData);
    closeExplorerModal();
  });

  document.getElementById('btn-inspector-duplicate').addEventListener('click', async () => {
    const fullData = await fetchProjectData(project.filename);
    if (fullData) await duplicateProject(fullData);
  });

  document.getElementById('btn-inspector-export').addEventListener('click', async () => {
    const fullData = await fetchProjectData(project.filename);
    if (fullData) exportProjectJson(fullData);
  });

  const btnDelete = document.getElementById('btn-inspector-delete');
  if (btnDelete) {
    btnDelete.addEventListener('click', () => {
      openDeleteConfirm(project.filename, project.name);
    });
  }
}

/**
 * Lấy danh sách dự án từ máy chủ (/api/projects)
 */
export async function fetchProjects(selectFilename = null) {
  try {
    const res = await fetch('/api/projects', {
      headers: getProjectApiHeaders()
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    state.projectsList = data.projects || [];

    const headerBadge = document.getElementById('header-project-count');
    if (headerBadge) {
      headerBadge.textContent = state.projectsList.length;
    }

    updateFilterCounts();
    renderProjectGrid();
    updateRecoveryUi();

    if (selectFilename) {
      selectProject(selectFilename);
    } else if (state.projectsList.length > 0 && !state.selectedProjectFilename) {
      selectProject(state.projectsList[0].filename);
    } else if (state.selectedProjectFilename) {
      const exists = state.projectsList.some((p) => p.filename === state.selectedProjectFilename);
      if (exists) {
        selectProject(state.selectedProjectFilename);
      } else if (state.projectsList.length > 0) {
        selectProject(state.projectsList[0].filename);
      } else {
        renderProjectInspector(null);
      }
    } else {
      renderProjectInspector(null);
    }
  } catch (err) {
    console.error('[fetchProjects Error]:', err);
    showToast(`Không thể nạp danh sách dự án: ${err.message}`, 'error');
  }
}

/**
 * Xuất file JSON
 */
export function exportProjectJson(project) {
  if (!project) return;
  const filename = project.filename || 'project.json';
  const jsonStr = JSON.stringify(project, null, 2);
  const blob = new Blob([jsonStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.json') ? filename : `${filename}.json`;
  a.click();
  URL.revokeObjectURL(url);
  showToast(`⬇️ Đã tải xuống tệp: ${a.download}`, 'success');
}

export function openDeleteConfirm(filename, name) {
  state.deleteTargetFilename = filename;
  state.deleteTargetName = name || filename;

  const nameEl = document.getElementById('delete-project-name');
  const fileEl = document.getElementById('delete-project-file');
  if (nameEl) nameEl.textContent = state.deleteTargetName;
  if (fileEl) fileEl.textContent = filename;

  const modal = document.getElementById('modal-confirm-delete');
  if (modal) modal.classList.add('active');
}

export function closeDeleteConfirm() {
  const modal = document.getElementById('modal-confirm-delete');
  if (modal) modal.classList.remove('active');
  state.deleteTargetFilename = null;
  state.deleteTargetName = null;
}

export async function executeDeleteProject() {
  if (!state.deleteTargetFilename) return;
  try {
    const res = await fetch(`/api/projects/${encodeURIComponent(state.deleteTargetFilename)}`, {
      method: 'DELETE',
      headers: getProjectApiHeaders()
    });
    if (!res.ok) throw new Error('Không thể xóa tệp');
    showToast(`🗑️ Đã xóa vĩnh viễn: ${state.deleteTargetName}!`, 'success');
    closeDeleteConfirm();
    await fetchProjects();
  } catch (err) {
    console.error('[executeDeleteProject Error]:', err);
    showToast(`❌ Lỗi xóa tệp: ${err.message}`, 'error');
  }
}

export function openExplorerModal() {
  const modal = document.getElementById('modal-project-explorer');
  if (modal) {
    modal.classList.add('active');
    initRecoveryKeyUi();
    updateRecoveryUi();
    fetchProjects();
  }
}

export function closeExplorerModal() {
  const modal = document.getElementById('modal-project-explorer');
  if (modal) modal.classList.remove('active');
}

export function toggleExplorerModal() {
  const modal = document.getElementById('modal-project-explorer');
  if (modal && modal.classList.contains('active')) {
    closeExplorerModal();
  } else {
    openExplorerModal();
  }
}
